import { ThemeKey } from "@smartsheet/design-tokens";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import React, { CSSProperties, forwardRef, HTMLAttributes, ReactNode, useState, useRef } from "react";
import { neutralLight40 } from "../../constants/colors";
import { useTheme } from "../../theme/utils/useTheme";
import {
    LoggableEvent,
    NotLoggedByDefault,
    useLoggingDecorator,
    useLoggingDisabledByDefault,
} from "../../util/logging";
import {
    ThemeColorNames,
    colorTheme,
    getColors,
    getBackground,
    getBackgroundHoverColor,
    avatarTheme,
} from "./avatarTheme";
import { avatarUtils } from "./avatarUtils";
import { AvatarBadge } from "./styled/AvatarBadge";
import { AvatarRoot } from "./styled/AvatarRoot";
import { Image } from "./styled/Image";
import { Initials } from "./styled/Initials";
import { SizeTypes, CustomColor } from "./types";

export interface AvatarProps extends Omit<HTMLAttributes<HTMLDivElement>, "color" | "content" | "onClick"> {
    /**
     * Defines the size of the avatar
     */
    size: SizeTypes;
    /** Props for the optional badge */
    badgeProps?: {
        /**
         * Icon to display in the badge. Must set 'currentColor' for color.
         */
        icon: ReactNode;
        /**
         * Necessary to set the correct border color for the badge to match the background of the page.
         */
        renderOn: "work-surface" | "canvas";
        /**
         * Sets the background and icon colors.
         */
        type: "error" | "success" | "warning" | "default";
    };
    /**  A classname to be applied to the root element of this component. */
    className?: string;

    /**
     * A `clientId` prop is provided for specified elements, which is a unique string that
     * appears as a data attribute `data-client-id` in the rendered code, serving as a hook
     * for automated tests
     */
    clientId?: string;

    /**
     * The color theme to apply, or string containing css value for `backgroundColor`. Please use
     * the `email` prop unless you have a good reason to choose a color yourself.
     */
    color?: ThemeColorNames | string | CustomColor;

    /**
     * An icon or other generic content within the Avatar. For text, please use the initials prop instead.
     */
    content?: React.ReactNode;

    /**
     * An email address associated with the user profile. This will be used to generate the color of
     * the avatar if no color prop is provided. Please only provide this prop or color, but not
     * both.
     */
    email?: string;

    /**
     * Initials associated with the user profile. Make sure your text is
     * either 2 or 3 characters, whatever fits the size you've chosen.
     * This prop will add a wrapper around the given string content to offset
     * some centering issues with TT Norms.
     */
    initials?: string;

    /**
     * Handler to be called on click.
     */
    onClick?: (event: React.MouseEvent, log: LoggableEvent<NotLoggedByDefault>) => void | Promise<void>;

    /**
     * An override for the aria role applied to this element.
     *
     * If you want to remove this component from the keyboard flow using tabIndex=-1, make
     * sure you set this to "presentation" and add aria-hidden.
     *
     * Defaults to "button".
     */
    role?: string;

    /**
     * If true, will show an inset ring around the Avatar, showing the user is currently active.
     * This is used for collaboration scenarios.
     */
    showRing?: boolean;

    /**
     * A url to load an image from (this can also be a base64 encoded image).
     */
    src?: string;

    /**
     * A value for the buttons tab index. Defaults to 0.
     *
     * If you want to remove this component from the keyboard flow using tabIndex=-1, make
     * sure you set role to "presentation" and add aria-hidden.
     */
    tabIndex?: number;
}

const colorIsThemeColor = (color: string): color is ThemeColorNames => color in colorTheme;

function parseColor(
    email: string | undefined,
    color: ThemeColorNames | string | CustomColor,
    theme: RequiredThemeKeys
) {
    if (email) {
        const emailColor = avatarUtils.emailToAvatarColor(email, theme.includes("ursa") ? "ursa" : "lodestar");
        return getColors(emailColor, theme);
    }
    if (typeof color === "object") {
        //when a CustomColor object is passed
        return {
            ...color,
            backgroundHoverColor: color.backgroundColor,
            borderColor: color.backgroundColor,
        };
    } else if (colorIsThemeColor(color)) {
        //when a valid ThemeColorNames is passed
        return getColors(color, theme);
    } else {
        // For back-compat with older code. Will not be accessible in all cases.
        return {
            backgroundColor: color,
            backgroundHoverColor: color,
            borderColor: color,
            textColor: neutralLight40,
        };
    }
}

/**
 * An avatar is a visual representation of a user or entity, typically using a profile image or initials.
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
    (
        {
            "aria-label": ariaLabel,
            badgeProps,
            className,
            color,
            clientId,
            content,
            email,
            initials,
            role = "button",
            showRing = false,
            size,
            src,
            onClick,
            tabIndex,
            ...rest
        },
        ref
    ) => {
        if (process.env.NODE_ENV !== "production") {
            if (typeof content === "string") {
                console.warn(
                    "Avatar: the content prop doesn't quite center text well due to nuances in" +
                        " TT Norms. Please use the initials prop instead."
                );
            }
            if (initials && content) {
                console.warn(
                    "Avatar: both content and initials were provided to the Avatar component," +
                        " content will take precedence. Please choose one or the other."
                );
            }
            if (!content && !initials && !src) {
                console.warn(
                    "Avatar: no content was provided to the Avatar component. Please include a" +
                        " value for src, content, or initials"
                );
            }
            if (email && color) {
                console.warn("Avatar: Please only provide a color OR an email. Not both.");
            }
        }
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        const localRef = useRef<HTMLDivElement | null>(null);
        const internalRef = useForkRef(ref, localRef);

        const [hasError, setError] = useState(false);

        const withLogging = useLoggingDecorator({
            controlType: "ava",
            clientId,
        });

        const onClickWithLogging = useLoggingDisabledByDefault(withLogging, { actionId: LogActionId.CLICK }, onClick);

        // Default to blue if no color is provided here instead of in the prop destructuring so the
        // warning messages above have the option to see an unset color.
        const colors = parseColor(email, color ?? "blue", themeMode);

        return (
            <AvatarRoot
                className={className}
                data-client-id={clientId}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={onClickWithLogging}
                role={role}
                size={size}
                ref={internalRef}
                tabIndex={tabIndex}
                aria-label={ariaLabel}
                showRing={showRing}
                themeMode={themeMode}
                style={
                    {
                        "--smar--lc--avatar--background": getBackground({
                            backgroundColor: colors.backgroundColor,
                            backgroundImage: src,
                            hasError,
                        }),
                        "--smar--lc--avatar--background--hover": getBackgroundHoverColor({
                            backgroundColor: colors.backgroundHoverColor,
                            hasBackgroundImage: !!src,
                            hasError,
                        }),
                        "--smar--lc--avatar--border-color": colors.borderColor,
                        "--smar--lc--avatar--text-color": colors.textColor,
                    } as CSSProperties
                }
                {...rest}
            >
                {src && !hasError && <Image src={src} alt="" aria-hidden={true} onError={() => setError(true)} />}
                {(!src || hasError) && content}
                {(!src || hasError) && !content && initials && <Initials>{initials}</Initials>}
                {badgeProps && (
                    <AvatarBadge
                        size={size === "medium" ? 18 : 22}
                        backgroundColor={avatarTheme.badge.backgroundColor[badgeProps.type](themeMode)}
                        color={avatarTheme.badge.iconColor[badgeProps.type](themeMode)}
                        themeMode={themeMode}
                        {...badgeProps}
                    >
                        {badgeProps.icon}
                    </AvatarBadge>
                )}
            </AvatarRoot>
        );
    }
);
Avatar.displayName = "Avatar";
